import { useRef } from 'preact/hooks';
import { api, mediaUrl } from '../api';
import { countryLabel, countryName } from '../countries';
import { makeT, type T } from '../i18n';
import { emptyDraft, errorText, getState, refreshAll, set, setDraft, stampById, toast } from '../state';
import type { AppState, ComposeDraft } from '../state';
import { StampArt } from '../stamp';
import { initialOf, tintFor } from '../util';
import { CameraIcon, UploadIcon } from './Icons';

/** Writing a postcard, in the design's two steps: the front, then the message. */
export function Compose({ s, t }: { s: AppState; t: T }) {
  const d = s.draft;

  return (
    <div class="panel panel-compose">
      <div class="tape" />
      <div class="compose-head">
        <div>
          <div class="title title-sm">{d.step === 0 ? t('stepFront') : t('stepNote')}</div>
          <div class="subtitle">{d.step === 0 ? t('stepFrontSub') : t('stepNoteSub')}</div>
        </div>
        <div class="step-dots" aria-hidden="true">
          <span data-on="true" />
          <span data-on={String(d.step === 1)} />
        </div>
      </div>

      <div class="compose-body">{d.step === 0 ? <FrontStep s={s} t={t} /> : <NoteStep s={s} t={t} />}</div>

      {d.step === 1 && (
        <div class="compose-foot">
          <button class="btn-back" onClick={() => setDraft({ step: 0 })}>
            {t('back')}
          </button>
          <button class="btn-send" data-ready={String(isReady(d))} onClick={() => void send(t)}>
            {t('send')}
          </button>
        </div>
      )}
    </div>
  );
}

function noteReady(d: ComposeDraft): boolean {
  return d.noteMode === 'scan' ? !!d.noteMedia : d.noteText.trim().length > 0;
}

function isReady(d: ComposeDraft): boolean {
  return noteReady(d) && !!d.stamp && d.recipient.trim().length > 2;
}

function FrontStep({ s, t }: { s: AppState; t: T }) {
  const input = useRef<HTMLInputElement>(null);
  const d = s.draft;
  const busy = s.uploading === 'front';
  const picked = !!d.frontMedia;

  return (
    <>
      <button class="dropzone" data-picked={String(picked)} disabled={busy} onClick={() => input.current?.click()}>
        <div class="dropzone-inner">
          {d.frontPreview && <img src={d.frontPreview} alt="" />}
          {d.frontPreview && <div class="dropzone-veil" />}
          <UploadIcon />
          <span class="dropzone-cap">{busy ? t('frontBusy') : picked ? t('frontReady') : t('frontIdle')}</span>
          <span class="dropzone-sub">{picked ? t('frontReadySub') : t('frontIdleSub')}</span>
        </div>
      </button>
      <input
        ref={input}
        class="visually-hidden"
        type="file"
        accept="image/*"
        tabIndex={-1}
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          e.currentTarget.value = '';
          if (file) void uploadFront(file, t);
        }}
      />
    </>
  );
}

async function uploadFront(file: File, t: T): Promise<void> {
  set({ uploading: 'front' });
  try {
    const { id } = await api.upload(file);
    setDraft({ frontMedia: id, frontPreview: mediaUrl(id) });
    // As in the design, a chosen photo moves straight on to the message.
    window.setTimeout(() => {
      const d = getState().draft;
      if (d.step === 0 && d.frontMedia === id) setDraft({ step: 1 });
    }, 480);
  } catch (err) {
    toast(errorText(err, t('genericError')), 'error');
  } finally {
    set({ uploading: null });
  }
}

function NoteStep({ s, t }: { s: AppState; t: T }) {
  const scanInput = useRef<HTMLInputElement>(null);
  const d = s.draft;
  const stamp = stampById(d.stamp);
  const scanned = d.noteMode === 'scan' && !!d.notePreview;

  return (
    <div class="writing-card">
      <div class="note-col">
        {scanned ? (
          <button class="scan-preview" onClick={() => setDraft({ noteMode: 'type' })}>
            <img src={d.notePreview ?? ''} alt="" />
            <span>{t('scannedNote')}</span>
          </button>
        ) : (
          <>
            <textarea
              class="note-input"
              placeholder={t('notePlaceholder')}
              aria-label={t('stepNote')}
              value={d.noteText}
              maxLength={2000}
              onInput={(e) => setDraft({ noteText: e.currentTarget.value })}
            />
            <button class="scan-btn" disabled={s.uploading === 'note'} onClick={() => scanInput.current?.click()}>
              <CameraIcon size={15} stroke={2.2} />
              <span>{t('uploadNote')}</span>
            </button>
          </>
        )}
        <input
          ref={scanInput}
          class="visually-hidden"
          type="file"
          accept="image/*"
          tabIndex={-1}
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            e.currentTarget.value = '';
            if (file) void uploadNote(file, t);
          }}
        />
      </div>

      <div class="addr-col">
        <div class="stamp-slot-row">
          <button
            class="stamp-slot"
            data-empty={String(!stamp)}
            aria-label={stamp ? stamp.label : `${t('stampBox1')} ${t('stampBox2')}`}
            onClick={() => set({ pickerOpen: true })}
          >
            {stamp ? (
              <StampArt key={stamp.id} stamp={stamp} />
            ) : (
              <div class="stamp-slot-empty">
                <span>{t('stampBox1')}</span>
                <span>{t('stampBox2')}</span>
              </div>
            )}
          </button>
        </div>
        <button class="addr" data-empty={String(d.recipient.trim().length < 3)} onClick={() => set({ addressOpen: true })}>
          <div class="addr-label">{t('to')}</div>
          <div class="addr-name" data-filled={String(!!d.recipient)}>
            {d.recipient || t('tapToChoose')}
          </div>
          <div class="addr-line">{d.toCountry ? countryLabel(d.toCountry, s.lang) : ''}</div>
          <div class="addr-line" />
          <div class="addr-foot">qué fue, pana? · {countryName(s.stampCountry, s.lang)}</div>
        </button>
      </div>
    </div>
  );
}

async function uploadNote(file: File, t: T): Promise<void> {
  set({ uploading: 'note' });
  try {
    const { id } = await api.upload(file);
    setDraft({ noteMedia: id, notePreview: mediaUrl(id), noteMode: 'scan' });
  } catch (err) {
    toast(errorText(err, t('genericError')), 'error');
  } finally {
    set({ uploading: null });
  }
}

let sending = false;

async function send(t: T): Promise<void> {
  const s = getState();
  const d = s.draft;
  if (sending) return;
  if (!noteReady(d)) return toast(t('needNote'), 'error');
  if (d.recipient.trim().length < 3) return toast(t('needRecipient'), 'error');
  if (!d.stamp) return toast(t('needStamp'), 'error');

  sending = true;
  try {
    const place = s.stampCountry;
    const { card } = await api.send({
      recipient: d.recipient,
      frontMedia: d.frontMedia,
      // Captions are stored in Spanish, like the rest of a card's printed text.
      frontLabel: makeT('es')('frontCaption', { place }),
      noteText: d.noteMode === 'scan' ? '' : d.noteText,
      noteMedia: d.noteMode === 'scan' ? d.noteMedia : null,
      stamp: d.stamp,
    });
    set({ sentCard: card, draft: emptyDraft() });
    void refreshAll();
  } catch (err) {
    toast(errorText(err, t('genericError')), 'error');
  } finally {
    sending = false;
  }
}

export function AddressBook({ s, t }: { s: AppState; t: T }) {
  const close = () => set({ addressOpen: false });

  return (
    <div class="scrim" role="dialog" aria-modal="true" aria-label={t('chooseWho')} onClick={close}>
      <div class="scrim-label">{t('chooseWho')}</div>
      <div class="book" onClick={(e) => e.stopPropagation()}>
        {s.friends.length === 0 ? (
          <div class="book-empty">{t('noFriendsToPick')}</div>
        ) : (
          s.friends.map((f) => (
            <button
              key={f.id}
              class="book-row"
              data-picked={String(s.draft.recipient === f.handle)}
              onClick={() => {
                setDraft({ recipient: f.handle, toCountry: f.country });
                close();
              }}
            >
              <span class="avatar avatar-sm" style={{ background: tintFor(f.handle) }}>
                {initialOf(f.handle)}
              </span>
              <span class="row-main">
                <span class="row-handle">{f.handle}</span>
                <span class="row-sub">{countryLabel(f.country, s.lang)}</span>
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export function StampPicker({ s, t }: { s: AppState; t: T }) {
  const close = () => set({ pickerOpen: false });

  return (
    <div class="scrim" role="dialog" aria-modal="true" aria-label={t('stampsFrom')} onClick={close}>
      <div class="scrim-label">
        {t('stampsFrom')} {countryName(s.stampCountry, s.lang)}
      </div>
      <div class="picker">
        {s.stamps.map((stamp) => (
          <button
            key={stamp.id}
            class="picker-stamp"
            data-picked={String(s.draft.stamp === stamp.id)}
            aria-label={stamp.label}
            onClick={(e) => {
              e.stopPropagation();
              setDraft({ stamp: stamp.id });
              close();
            }}
          >
            <StampArt stamp={stamp} />
          </button>
        ))}
      </div>
      <div class="scrim-hint">{t('tapStamp')}</div>
    </div>
  );
}
